import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';

import {Asset, AssetGroup} from '@deviceinsight/ng-ui-api-client';
import {Modal, Button} from '@deviceinsight/ng-ui-basic-components';
import {ReactableColumnDef} from '@deviceinsight/ng-ui-components';

import {AssetAndGroupsAndGateway, Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import AssetConnectionState from './AssetConnectionState';
import AssetTypeWithGatewayBubble from './AssetTypeWithGatewayBubble';
import loadable from '../../utils/loadable';

const AssetDataProcessingDefinitionPage = loadable({
	loader: () => import('./AssetDataProcessingDefinitionPage')
});
import assetIcon from '../../assets/icon_asset.svg';
import gatewayIcon from '../../assets/icon_gateway.svg';
import {assetFields} from '../search/fields';
import FilterTable from '../search/FilterTable';
import {SearchConfig} from '../search/types';
import {searchAssetsWithGroupsAndGateway} from '../search/asset/search';

const SEARCH_CONTEXT = 'assetGroupAssignedAssets';

const getColumnDefs = (
	onClick: (asset: AssetAndGroupsAndGateway) => any
): Array<ReactableColumnDef<AssetAndGroupsAndGateway>> => [
	{
		name: 'name',
		label: <Trans i18nKey="asset.name">Name</Trans>,
		sortable: true,
		render: ({asset}) => asset.name,
		width: '15%',
		onClick
	},
	{
		name: 'isGateway',
		label: <Trans i18nKey="asset.type">Type</Trans>,
		sortable: true,
		render: ({asset, gateway}: AssetAndGroupsAndGateway) => (
			<AssetTypeWithGatewayBubble asset={asset} gateway={gateway} />
		),
		width: '10%',
		onClick
	},
	{
		name: 'connection',
		label: <Trans i18nKey="asset.connection">Connection</Trans>,
		sortable: false,
		render: ({asset}: AssetAndGroupsAndGateway) => <AssetConnectionState asset={asset} showStateDescription />,
		width: '10%',
		onClick
	}
];

const getSearchConfig = (assetGroupId: number | null | undefined): SearchConfig<AssetAndGroupsAndGateway> => {
	return {
		fields: assetFields,
		search: (query, paginationSettings) => {
			let fullQuery;

			if (assetGroupId) {
				fullQuery = `${query} assetGroupId:${assetGroupId}`;
			} else {
				fullQuery = query;
			}

			return searchAssetsWithGroupsAndGateway(fullQuery, paginationSettings);
		}
	};
};

type Props = {
	assetGroup: AssetGroup;
	onSelectAsset: (arg0: AssetAndGroupsAndGateway) => any;
	title?: JSX.Element | string;
};

const AssetGroupDataProcessingDefinition = ({assetGroup}: Props) => {
	const history = useHistory();
	const [selectedAsset, setSelectedAsset] = useState<Asset | null | undefined>(undefined);
	const [isSelecting, setIsSelecting] = useState<boolean>(true);

	const onSelectAsset = (assetAndGroupsAndGateway: AssetAndGroupsAndGateway) => {
		const {asset} = assetAndGroupsAndGateway;
		setSelectedAsset(asset);
		setIsSelecting(false);
	};

	const columnDefs: Array<ReactableColumnDef<AssetAndGroupsAndGateway>> = getColumnDefs(onSelectAsset);
	const searchConfig: SearchConfig<AssetAndGroupsAndGateway> = getSearchConfig(assetGroup.id);

	const showSelectionModal = () => setIsSelecting(true);

	const hideSelectionModal = () => {
		if (selectedAsset === undefined) {
			history.push('../../dataprocessingdefinitions');
		} else {
			setIsSelecting(false);
		}
	};

	return (
		<>
			{selectedAsset && (
				<AssetDataProcessingDefinitionPage asset={selectedAsset}>
					<div className="dataprocessingdefinition-context">
						<span className="dataprocessingdefinition-context-text">
							<Trans i18nKey="assetgroups.detail.dataProcessingDefinition.context">on Asset</Trans>
						</span>
						<Button onClick={showSelectionModal}>
							<img
								src={selectedAsset.isGateway ? gatewayIcon : assetIcon}
								alt="icon"
								className="dataprocessingdefinition-context-icon"
							/>
							{selectedAsset.name}
						</Button>
					</div>
				</AssetDataProcessingDefinitionPage>
			)}
			{isSelecting && (
				<Modal
					header={
						<Trans i18nKey="assetgroups.detail.dataProcessingDefinition.select.title">Select Asset</Trans>
					}
					content={
						<React.Fragment>
							<FilterTable
								searchContext={SEARCH_CONTEXT}
								searchConfig={searchConfig}
								columnDefinitions={columnDefs}
								pageSize={10}
								uniqueItemKey={(asset) => asset.id}
							/>
						</React.Fragment>
					}
					footer={
						<Button onClick={hideSelectionModal}>
							<Trans i18nKey="assets.detail.dataProcessingDefinition.cancelButtonLabel">Cancel</Trans>
						</Button>
					}
					onOutsideClick={hideSelectionModal}
				/>
			)}
		</>
	);
};

export default AssetGroupDataProcessingDefinition;
